<template>
	<BarArrowButton :large="true" :flex="true" :title="this.title ? this.titleLabel : null">
		<v-row class="barArrowButtonSlotContent">
			<v-col class="centered" v-if="!selectedTotals">
				{{ waitMessage }}
				<v-progress-linear height="2px" indeterminate></v-progress-linear>
			</v-col>
		</v-row>
		<div :class="'barArrowButtonSlotContent ' + (small ? 'small' : '')" v-if="selectedTotals">
			<v-row>
				<Bar
					ref="spendChart"
					id="spend-chart-id"
					:options="chartOptions"
					:data="chartData"
					:height="chartHeight"
				/>
			</v-row>
			<v-row class="labelRow">
				<v-col class="labelCol centered">In: {{ formatter.format(Math.round(totalIn), false, false).replace('-', '') }}</v-col>
				<v-col class="labelCol centered">Pay: {{ this.payIn() }} </v-col>
				<v-col class="labelCol centered">Out: {{ formatter.format(Math.round(totalOut), false, false).replace('-', '') }}</v-col>
			</v-row>
		</div>
	</BarArrowButton>
</template>

<script>
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import { GRANDPARENT_CLASSIFICATIONS } from '../../mixins/SpendingAnalysis';
import BarArrowButton from './BarArrowButton.vue';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

export default {
	name: "AnalysisWidget",
	components: { Bar, BarArrowButton },
	props: {
		spendingAnalysisStructured: {
			type: Object,
			required: true
		},
		selectedSlotKey: { type: String, required: true },
		fakeTotals: { type: Boolean, required: false, default: false },
		formatter: { type: Object, required: true },
		title: { type: Boolean, required: false, default: false },
		titleLabel: { type: String, required: false, default: 'Spending Analysis' },
		titleSize: { type: String, required: false, default: 'normal' }, // normal | small
		waitMessage: { type: String, required: false, default: 'Loading...' },
		chartHeight: { type: Number, required: false, default: 150 },
		small: { type: Boolean, required: false, default: false },
	},
	data() {
		return {
			allEveryDaySpend: [],
			allCommittedCosts: [],
			allFuturePlans: [],
			allIncome: [],
			selectedTotals: null,
			titleFontSize: '20px',
			chartData: {
				labels: [ 'Income', 'Spend' ],
				datasets: [ 
					{ 
						label: 'Income',
						backgroundColor: '#B4D8D7',
						data: [],
						borderRadius: 10
					},
					{
						label: 'Fixed Costs',
						backgroundColor: '#413655',
						data: [],
						borderRadius: 10
					},
					{
						label: 'Everyday Spend',
						backgroundColor: '#E3E6FA',
						data: [],
						borderRadius: 10
					},
					{
						label: 'Future Planning',
						backgroundColor: '#F0D8CF',
						data: [],
						borderRadius: 10
					}
				],
			},
			chartOptions: {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					x: {
						stacked: true,
						grid: {
							display: false 
						},
						ticks: {
							display: false 
						}
					},					
					y: {
						stacked: true,
						grid: {
							display: true 
						},
						ticks: {
							display: true,
							maxTicksLimit: this.small ? 3 : 5,
							callback: (value) => {
								if (value >= 1000) {
									return this.formatter.format(Math.round(value / 1000), true, false) + 'k';
								}
								return this.formatter.format(value, true, false);
							}
						}
					}
				},
				plugins: {
					legend: {
						display: false
					},
					title: {
						display: false
					},
					tooltip: {
						callbacks: {
							label: function(context) {
								let label = context.dataset.label || '';
								if (label) {
									label += ': ';
								}
								if (context.parsed.y !== null) {
									label += '£' + context.parsed.y.toLocaleString();
								}
								return label;
							}
						}
					}
				}
			},
			totalIn: 0,
			totalOut: 0
		}	
	},
	watch: {
		spendingAnalysisStructured: {
			immediate: true,
			handler(newVal) {
				if (newVal && newVal.grandparentAnalysis) {
					this.getStructuredCategories();
				}
			}
		},
		selectedSlotKey: {
			handler(newVal) {
				if (newVal && this.allIncome.length > 0) { 
					this.updateSelectedTotals();
					this.updateChart();
				}
			}
		}
	},
	methods: {
		onLoad() {
			this.getStructuredCategories();
			if (this.titleSize === 'small') {
				this.titleFontSize = '16px';
			}
		},
		payIn() {
			if (this.fakeTotals) {
				return '25 days';
			}
			var daysRemaining = this.spendingAnalysisStructured.daysTillCycleResets ? this.spendingAnalysisStructured.daysTillCycleResets : 0;
			var pluralLabel = daysRemaining === 1 ? 'day' : 'days';
			return daysRemaining + ' ' + pluralLabel;
		},
		getStructuredCategories() {
			if (this.fakeTotals) {
				this.allEveryDaySpend = [];
				this.allCommittedCosts = [];
				this.allFuturePlans = [];
				this.allIncome = [];
				this.selectedTotals = this.getMockSpendingTotals();
				this.updateChart();
				return;
			}
			this.allEveryDaySpend = this.spendingAnalysisStructured.grandparentAnalysis.filter(item => item.grandparent === GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND);
			this.allCommittedCosts = this.spendingAnalysisStructured.grandparentAnalysis.filter(item => item.grandparent === GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS);
			this.allFuturePlans = this.spendingAnalysisStructured.grandparentAnalysis.filter(item => item.grandparent === GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING);
			this.allIncome = this.spendingAnalysisStructured.grandparentAnalysis.filter(item => item.grandparent === GRANDPARENT_CLASSIFICATIONS.INCOME);
			
			if (this.selectedSlotKey) {
				this.updateSelectedTotals();
				this.updateChart();
			}
		},
		updateSelectedTotals() {
			this.selectedTotals = [];
			
			// Calculate selectedIncome
			var selectedIncome = this.allIncome.find(s => s.slotName === this.selectedSlotKey);
			if (!selectedIncome) {
				selectedIncome = { totalIn: 0, totalOut: 0, totalNet: 0, groupedAnalysis: [] };
			}

			// Calculate other categories using simplified logic
			var selectedFuturePlans = this.getSelectedCategoryForTotals(this.allFuturePlans);
			var selectedEverydaySpend = this.getSelectedCategoryForTotals(this.allEveryDaySpend);
			var selectedCommittedCosts = this.getSelectedCategoryForTotals(this.allCommittedCosts);

			this.selectedTotals.push({
				"id": GRANDPARENT_CLASSIFICATIONS.INCOME, 
				"Amount": Math.round(Math.abs(selectedIncome.totalIn))
			});
			this.selectedTotals.push({
				"id": GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING,
				"Amount": Math.round(Math.abs(selectedFuturePlans.totalNet))
			});
			this.selectedTotals.push({
				"id": GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND,
				"Amount": Math.round(Math.abs(selectedEverydaySpend.totalNet))
			});
			this.selectedTotals.push({
				"id": GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS,
				"Amount": Math.round(Math.abs(selectedCommittedCosts.totalNet))
			});
		},
		updateChart() {
			if (!this.selectedTotals || this.selectedTotals.length === 0) return;
			
			var income = this.selectedTotals[GRANDPARENT_CLASSIFICATIONS.INCOME].Amount;
			var fixedCosts = this.selectedTotals[GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS].Amount;
			var everyDaySpend = this.selectedTotals[GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND].Amount;
			var futurePlanning = this.selectedTotals[GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING].Amount;
			
			this.chartData.datasets[0].data = [
				income,
				0 // Placeholder for Spend
			];
			this.chartData.datasets[1].data = [
				0, // Placeholder for Income
				fixedCosts
			];
			this.chartData.datasets[2].data = [
				0, // Placeholder for Income
				everyDaySpend
			];
			this.chartData.datasets[3].data = [
				0, // Placeholder for Income
				futurePlanning
			];
			this.totalIn = income;
			this.totalOut = fixedCosts + everyDaySpend + futurePlanning;

			// Create a deep copy of chartData to trigger reactivity
			this.chartData = JSON.parse(JSON.stringify(this.chartData));

			// Update the chart
			this.$nextTick(() => {
				if (this.$refs.spendChart && this.$refs.spendChart.chart) {
					this.$refs.spendChart.chart.update();
				}
			});
		},
		getSelectedCategoryForTotals(categoryArray) {
			var selected = categoryArray.find(s => s.slotName === this.selectedSlotKey);
			if (!selected) {
				selected = { totalIn: 0, totalOut: 0, totalNet: 0, groupedAnalysis: [] };
			}
			return selected;
		},
		getMockSpendingTotals() {
			var totals = [];
			totals.push(
				{
					"id": GRANDPARENT_CLASSIFICATIONS.INCOME, 
					"Amount": Math.round(2000)
				});
			totals.push(
				{
					"id": GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING,
					"Amount": Math.round(100)
				});
			totals.push(
				{
					"id": GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND,
					"Amount": Math.round(400)
				});
			totals.push(
				{
					"id": GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS,
					"Amount": Math.round(900)
				});
			return totals;
		},
	},
	mounted: function() { this.onLoad(); }
}
</script>

<style scoped>

.barArrowButtonSlotContent {
	padding: 20px;
	padding-top: 12px;
	padding-bottom: 5px;
}

.analysis-widget {
	border-radius: var(--border-radius-m);
	padding: 15px;
	border-color: var(--border-color);
	border: 0px solid var(--border-color);
	height: 100%;
	background-color: var(--background-color);
	width: 100%;
	box-shadow: 0px 4px 8px 0px rgba(51, 40, 71, 0.05);
}

.title-col {
	font-size: 20px;
	white-space: nowrap;
	margin-top: auto;
	margin-bottom: auto;
}

.arrow-col {
	text-align: right;
	padding-left: 0px;
	margin-top: auto;
	margin-bottom: auto;
}

.arrow {
	height: 10px;
}

.labelRow, .labelCol {
	padding-top: 0px;
}

.small {
	font-size: 9px !important;
}

</style>